import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Phone, Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navigation = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#services', label: t('nav.services') },
    { href: '#about', label: t('nav.about') },
    { href: '#testimonials', label: t('nav.testimonials') },
    { href: '#location', label: t('nav.location') },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? 'nav-glass scrolled border-b border-earth-dark' : 'nav-glass'
      }`}
      role="banner"
    >
      <nav 
        className="container-main flex items-center justify-between h-20"
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <a 
          href="#" 
          className="flex flex-col"
          data-testid="logo-link"
          aria-label="Healing Clinic - Home"
        >
          <span className="font-heading text-2xl font-bold text-[#1C1C1E]">
            Healing Clinic
          </span>
          <span className="text-sm text-muted-foreground font-body">
            힐링한의원
          </span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-[#1C1C1E] hover:text-sage font-body font-medium transition-colors duration-200"
              data-testid={`nav-link-${link.href.slice(1)}`}
            >
              {link.label}
            </a>
          ))}

          {/* Language Toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="lang-toggle flex items-center gap-2"
                data-testid="language-toggle"
                aria-label={`Current language: ${language === 'en' ? 'English' : 'Tiếng Việt'}`}
              >
                <Globe className="w-4 h-4" />
                <span>{language === 'en' ? 'EN' : 'VI'}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => setLanguage('en')}
                data-testid="lang-option-en"
              >
                English
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setLanguage('vi')}
                data-testid="lang-option-vi"
              >
                Tiếng Việt
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* CTA Button */}
          <a 
            href="tel:+17145915360"
            className="btn-primary-custom flex items-center gap-2 text-sm"
            data-testid="nav-cta-button"
          >
            <Phone className="w-4 h-4" />
            <span>{t('nav.bookNow')}</span>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-[#1C1C1E]"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-expanded={isMobileMenuOpen}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          data-testid="mobile-menu-button"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden bg-cream border-t border-earth-dark"
          role="menu"
        >
          <div className="container-main py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-[#1C1C1E] hover:text-sage font-body font-medium py-2 transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                role="menuitem"
              >
                {link.label}
              </a>
            ))}
            
            {/* Mobile Language Toggle */}
            <div className="flex items-center gap-4 py-2">
              <span className="text-muted-foreground font-body text-sm">Language:</span>
              <Button 
                variant={language === 'en' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('en')}
                className={language === 'en' ? 'bg-sage hover:bg-sage-dark' : ''}
                data-testid="mobile-lang-en"
              >
                EN
              </Button>
              <Button 
                variant={language === 'vi' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('vi')}
                className={language === 'vi' ? 'bg-sage hover:bg-sage-dark' : ''}
                data-testid="mobile-lang-vi"
              >
                VI
              </Button>
            </div>

            {/* Mobile CTA */}
            <a 
              href="tel:+17145915360"
              className="btn-primary-custom flex items-center justify-center gap-2 mt-2"
              data-testid="mobile-cta-button"
            >
              <Phone className="w-4 h-4" />
              <span>{t('hero.phone')}</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
